/**
 * Shared schemas for Jemmi.
 *
 * These Zod schemas are the single source of truth for data shapes that flow
 * between the web client and the Workers API. Define a schema once here, and
 * both sides get:
 *   - runtime validation (parse incoming requests/responses)
 *   - compile-time TypeScript types (via z.infer)
 *
 * Rule of thumb: if it crosses the network, it lives here.
 */
import { z } from 'zod';

// ──────────────────────────────────────────────────────────────────────────
// Primitives
// ──────────────────────────────────────────────────────────────────────────

/** Categories that a challenge can belong to. Admin-managed in production. */
export const categorySchema = z.enum(['Photography', 'Cooking', 'Singing', 'Cars', 'Music']);
export type Category = z.infer<typeof categorySchema>;

export const CATEGORIES = categorySchema.options;

// TODO: Paid-tier feature (not in the free app) — subcategories nested
// under a Category, e.g. Photography > Portrait / Landscape / Street.
// When this ships:
//   - challengeSchema gains an optional `subcategory` field, validated
//     against whatever subcategories the chosen category allows.
//   - Challenge creation (CreateChallengePage) needs a tier choice up
//     front: "Create a free challenge" (single category, current
//     behavior, no payment) vs "Create a paid challenge" (adds
//     subcategory selection, gated behind checkout).
//   - Paid checkout needs a real payment integration — current best
//     practice is a tokenized processor (e.g. Stripe, Braintree) so raw
//     card numbers never touch our servers (PCI SAQ A scope, not
//     SAQ D), plus mobile wallet support (Apple Pay / Google Pay via
//     the Payment Request API) alongside card entry. See the matching
//     note in PROJECT_NOTES.md's "Next Ideas / TODO" for the fuller
//     writeup — this comment is the searchable pointer to it.

export const submissionStatusSchema = z.enum([
  'approved', // passed all moderation, visible to users
  'flagged_by_user', // flagged by user(s), temporarily hidden until moderation review
  'pending_moderation', // queued for Azure Content Safety automated check
  'removed', // rejected by moderator or failed auto-moderation, permanently hidden
]);
export type SubmissionStatus = z.infer<typeof submissionStatusSchema>;

/** Reasons a user can flag a submission. */
export const flagReasonSchema = z.enum(['sexual', 'violent', 'hate_speech', 'harassment', 'other']);
export type FlagReason = z.infer<typeof flagReasonSchema>;

// ──────────────────────────────────────────────────────────────────────────
// User
// ──────────────────────────────────────────────────────────────────────────

/** Number of violations after which an account is automatically locked. */
export const CONTENT_VIOLATION_LOCKOUT_THRESHOLD = 3;

/**
 * Admin accounts (isAdmin or isSuperAdmin) are restricted to this email
 * domain for now — enforced structurally below via userSchema's .refine()
 * so it holds everywhere a User is validated, not just at one call site.
 * Regular users (isAdmin: false) are unrestricted; this only gates who
 * can hold elevated access.
 */
export const ADMIN_EMAIL_DOMAIN = 'jemmi.fun';

const baseUserSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  displayName: z.string().min(1).max(40),
  avatarUrl: z.string().url().nullable(),
  createdAt: z.string().datetime(),
  isAdmin: z.boolean().default(false),
  /**
   * Elevated tier above isAdmin. Regular admins moderate challenges and
   * content (see AdminDashboardPage). Only super admins can see
   * cross-account security data like the locked-accounts list, since
   * that's every locked user's email in one place — not something every
   * moderator needs routine access to.
   */
  isSuperAdmin: z.boolean().default(false),
  isLocked: z.boolean().default(false),
  /**
   * Count of this user's own submissions removed for violating content
   * guidelines (creator-reviewed removals and automated severe-content
   * removals both count). isLocked is set automatically once this
   * reaches CONTENT_VIOLATION_LOCKOUT_THRESHOLD — see the 'Content
   * violation tracking' section in apps/api/src/lib/moderation.ts.
   */
  contentViolationCount: z.number().int().nonnegative().default(0),
});

export const userSchema = baseUserSchema.refine(
  (user) => (!user.isAdmin && !user.isSuperAdmin) || user.email.endsWith(`@${ADMIN_EMAIL_DOMAIN}`),
  {
    message: `Admin accounts must use an @${ADMIN_EMAIL_DOMAIN} email address`,
    path: ['email'],
  },
);
export type User = z.infer<typeof userSchema>;

/** A trimmed-down user shape safe to expose in public API responses. */
export const publicUserSchema = baseUserSchema.pick({
  id: true,
  displayName: true,
  avatarUrl: true,
});
export type PublicUser = z.infer<typeof publicUserSchema>;

// ──────────────────────────────────────────────────────────────────────────
// Challenge
// ──────────────────────────────────────────────────────────────────────────

export const challengeSchema = z.object({
  id: z.string().uuid(),
  title: z.string().min(3).max(120),
  brief: z.string().min(10).max(500),
  category: categorySchema,
  creatorId: z.string().uuid(),
  creator: publicUserSchema,
  createdAt: z.string().datetime(),
  endsAt: z.string().datetime(),
  submissionCount: z.number().int().nonnegative(),
  jemCount: z.number().int().nonnegative(),
  hasVoted: z.boolean(),
});
export type Challenge = z.infer<typeof challengeSchema>;

/** Payload for creating a challenge — derived from challengeSchema. */
export const createChallengeSchema = challengeSchema.pick({
  title: true,
  brief: true,
  category: true,
  endsAt: true,
});
export type CreateChallengeInput = z.infer<typeof createChallengeSchema>;

// ──────────────────────────────────────────────────────────────────────────
// Submission
// ──────────────────────────────────────────────────────────────────────────

export const submissionMediaTypeSchema = z.enum(['image', 'video']);
export type SubmissionMediaType = z.infer<typeof submissionMediaTypeSchema>;

export const submissionSchema = z.object({
  id: z.string().uuid(),
  challengeId: z.string().uuid(),
  userId: z.string().uuid(),
  user: publicUserSchema,
  mediaUrl: z.string().url(),
  mediaType: submissionMediaTypeSchema,
  caption: z.string().max(280).nullable(),
  createdAt: z.string().datetime(),
  jemCount: z.number().int().nonnegative(),
  hasVoted: z.boolean(),
  status: submissionStatusSchema.default('approved'),
});
export type Submission = z.infer<typeof submissionSchema>;

/** Payload for creating a submission. */
export const createSubmissionSchema = submissionSchema.pick({
  challengeId: true,
  mediaUrl: true,
  mediaType: true,
  caption: true,
});
export type CreateSubmissionInput = z.infer<typeof createSubmissionSchema>;

/** Moderation result from Azure Content Safety. */
export const moderationResultSchema = z.object({
  adult: z.object({ score: z.number() }).optional(),
  violence: z.object({ score: z.number() }).optional(),
  hate: z.object({ score: z.number() }).optional(),
  selfHarm: z.object({ score: z.number() }).optional(),
  textResults: z
    .object({ hate: z.number().optional(), violence: z.number().optional() })
    .optional(),
  flagged: z.boolean(),
  reason: z.string().optional(),
});
export type ModerationResult = z.infer<typeof moderationResultSchema>;

// ──────────────────────────────────────────────────────────────────────────
// Flag Reports
// ──────────────────────────────────────────────────────────────────────────

/** User flag report on a submission. */
export const flagReportSchema = z.object({
  id: z.string().uuid(),
  submissionId: z.string().uuid(),
  reportedByUserId: z.string().uuid(),
  reason: flagReasonSchema,
  details: z.string().max(500).nullable(),
  createdAt: z.string().datetime(),
  moderatorNote: z.string().nullable(),
  resolvedAt: z.string().datetime().nullable(),
});
export type FlagReport = z.infer<typeof flagReportSchema>;

/** Payload for creating a flag report. */
export const createFlagReportSchema = z.object({
  submissionId: z.string().uuid(),
  reason: flagReasonSchema,
  details: z.string().max(500).nullable().optional(),
});
export type CreateFlagReportInput = z.infer<typeof createFlagReportSchema>;

// ──────────────────────────────────────────────────────────────────────────
// Notifications
//
// This app has three user roles a notification can be about: challenge
// CREATOR, SUBMITTER, and ADMIN/SUPER ADMIN. Every notification type below
// must go to exactly the role(s) noted, and message copy must be worded
// for that role — "your challenge" for a creator, "your submission" for a
// submitter, never generic phrasing that could read as either.
//
// Recipient matrix:
//   submission_flagged       → CREATOR    (actionable: approve/remove)
//   new_submission            → CREATOR    (informational)
//   challenge_ending_soon      → CREATOR or SUBMITTER, depending on the
//                                 recipient's relationship to the
//                                 challenge — a creator gets "your
//                                 challenge ends soon, wrap up review";
//                                 a submitter/participant gets "a
//                                 challenge you entered ends soon, last
//                                 call to vote". Generate the copy from
//                                 which role the specific recipient has,
//                                 never a one-size-fits-all string.
//   outjemmed                 → SUBMITTER  (about their own submission —
//                                 the referenced submission must belong
//                                 to the recipient; never reference
//                                 someone else's)
//   submission_removed        → SUBMITTER  (the person whose content was
//                                 removed — carries violationCount)
//   submission_auto_removed   → CREATOR    (informational counterpart to
//                                 submission_removed for the SAME removal
//                                 event — the creator is told a
//                                 submission was auto-removed with no
//                                 media attached and nothing for them to
//                                 do; the submitter separately gets
//                                 submission_removed with the violation
//                                 consequences). These are two distinct
//                                 notifications to two distinct people
//                                 for one event, not one notification
//                                 shown to both.
//
// ADMIN/SUPER ADMIN intentionally receive none of these as push
// notifications. Regular admins pull from AdminDashboardPage (the
// platform-wide moderation queue and flagged-content queue); super
// admins pull from LockedAccountsPage. Per-item push notifications to
// every admin for every flag across the whole platform would be noisy
// and doesn't scale — the dashboards are the intended admin surface.
// ──────────────────────────────────────────────────────────────────────────

export const notificationTypeSchema = z.enum([
  /** A submission on your challenge was flagged by the community — needs your review. */
  'submission_flagged',
  /** Someone submitted an entry to a challenge you created. */
  'new_submission',
  /** A challenge you created or entered ends within the next few hours. */
  'challenge_ending_soon',
  /** Another submission passed yours in jem count on a challenge you entered. */
  'outjemmed',
  /**
   * Sent to the SUBMITTER (not the challenge creator) when one of their
   * submissions is removed for violating content guidelines — whether by
   * a creator's manual decision or automated moderation. Carries a
   * running violationCount (see NotificationDetail) so the submitter
   * knows how close they are to CONTENT_VIOLATION_LOCKOUT_THRESHOLD.
   */
  'submission_removed',
  /**
   * Sent to the CHALLENGE CREATOR (not the submitter) when automated
   * moderation removes a submission from their challenge — the creator-
   * facing counterpart to 'submission_removed', for the same event.
   * Purely informational: no media reference is ever attached (see the
   * CSAM/severity notes in apps/api/src/lib/moderation.ts) and there's
   * nothing for the creator to act on, since the removal already
   * happened automatically.
   */
  'submission_auto_removed',
]);
export type NotificationType = z.infer<typeof notificationTypeSchema>;

export const notificationActionSchema = z.enum(['approved', 'removed']).nullable();
export type NotificationAction = z.infer<typeof notificationActionSchema>;

export const notificationSchema = z.object({
  id: z.string().uuid(),
  recipientId: z.string().uuid(),
  /** Only set for notification types about a specific submission. */
  submissionId: z.string().uuid().nullable(),
  challengeId: z.string().uuid(),
  type: notificationTypeSchema,
  message: z.string(),
  createdAt: z.string().datetime(),
  readAt: z.string().datetime().nullable(),
  /** Only meaningful for 'submission_flagged' — the other types are informational. */
  actionTaken: notificationActionSchema,
  actionTakenAt: z.string().datetime().nullable(),
});
export type Notification = z.infer<typeof notificationSchema>;

/** Detailed notification with submission and challenge info for display. */
export const notificationDetailSchema = notificationSchema.extend({
  /** Null unless the notification is about a specific submission. */
  submission: submissionSchema.nullable(),
  challenge: challengeSchema,
  /** Only nonzero for 'submission_flagged'. */
  flagReportCount: z.number().int().nonnegative().default(0),
  /**
   * Only set for 'submission_removed' — the submitter's total
   * contentViolationCount immediately after this removal, out of
   * CONTENT_VIOLATION_LOCKOUT_THRESHOLD.
   */
  violationCount: z.number().int().nonnegative().optional(),
});
export type NotificationDetail = z.infer<typeof notificationDetailSchema>;

// ──────────────────────────────────────────────────────────────────────────
// Auth
// ──────────────────────────────────────────────────────────────────────────

export const loginSchema = z.object({
  email: z.string().email(),
  /**
   * TODO: require a real minimum (e.g. .min(8)) before production.
   * Blank passwords are temporarily allowed so the three named test
   * accounts (admin@jemmi.fun, contentcreator@jemmi.fun,
   * enduser@jemmi.fun — see mockData.ts) can be used for role-based
   * testing without a real credential system in place yet.
   */
  password: z.string().max(128),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const registerSchema = loginSchema.extend({
  displayName: z.string().min(1).max(40),
});
export type RegisterInput = z.infer<typeof registerSchema>;

export const authResponseSchema = z.object({
  user: userSchema,
  token: z.string(),
});
export type AuthResponse = z.infer<typeof authResponseSchema>;

// ──────────────────────────────────────────────────────────────────────────
// API envelope
// All API responses follow this shape so the client can branch on `ok`.
// ──────────────────────────────────────────────────────────────────────────

export const apiErrorSchema = z.object({
  ok: z.literal(false),
  error: z.object({
    code: z.string(),
    message: z.string(),
  }),
});
export type ApiError = z.infer<typeof apiErrorSchema>;

export const apiSuccessSchema = <T extends z.ZodTypeAny>(data: T) =>
  z.object({
    ok: z.literal(true),
    data,
  });

// ──────────────────────────────────────────────────────────────────────────
// Category Requests
// ──────────────────────────────────────────────────────────────────────────

export const categoryRequestStatusSchema = z.enum(['pending', 'approved', 'denied']);
export type CategoryRequestStatus = z.infer<typeof categoryRequestStatusSchema>;

export const categoryRequestSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  categoryName: z.string().min(1).max(40),
  description: z.string().max(200),
  status: categoryRequestStatusSchema,
  createdAt: z.string().datetime(),
  reviewedAt: z.string().datetime().nullable(),
  reviewedBy: z.string().uuid().nullable(),
  adminNote: z.string().nullable(),
});
export type CategoryRequest = z.infer<typeof categoryRequestSchema>;

export const createCategoryRequestSchema = z.object({
  categoryName: z.string().min(1).max(40),
  description: z.string().max(200),
});
export type CreateCategoryRequestInput = z.infer<typeof createCategoryRequestSchema>;
